/**
 * Tracking initialization and property extraction
 * 
 * MODIFY THIS FILE FOR:
 * - Adding new tracking features (initXxxTracking methods)
 * - Feature-specific property extraction (extractXxxProperties)
 * 
 * DON'T MODIFY THIS FILE FOR:
 * - Core tracking logic → tracking-core.js
 * - Debug utilities → tracking-debug.js
 * - Event constants → tracking-constants.js
 * 
 * QUICK START:
 * 1. Add event to tracking-constants.js
 * 2. Create initXxxTracking() method here
 * 3. Call it in init()
 * 4. Use Tracking.trackStandardEvent() or TrackingCore helpers
 */
import { TRACKING_EVENTS } from './tracking-constants.js';
import TrackingCore from './tracking-core.js';
import TrackingDebug from './tracking-debug.js';

const Tracking = {
    /**
     * Track event with standard properties (page_type, page_url)
     * Wrapper around TrackingCore.trackStandardEvent()
     * @param {string} eventName - Event name from TRACKING_EVENTS
     * @param {object} eventProperties - Additional properties
     */
    trackStandardEvent(eventName, eventProperties = {}) {
        TrackingCore.trackStandardEvent(eventName, eventProperties);
    },

    /**
     * Property Extraction Methods
     * Feature-specific data extraction - modify when adding new features
     */
    
    extractProviderProperties(element) {
        const props = {};
        ['providerId', 'providerName', 'providerCountry', 'providerCity', 'providerType', 'buttonLocation'].forEach(key => {
            const datasetKey = key.charAt(0).toLowerCase() + key.slice(1).replace(/([A-Z])/g, '_$1').toLowerCase();
            props[datasetKey] = element.dataset[key] || (key === 'buttonLocation' ? 'unknown' : null);
        });
        return props;
    },

    extractArticleProperties() {
        if (typeof window.pe_mp_article_data === 'undefined') return {};
        
        const data = window.pe_mp_article_data;
        const properties = {
            article_id: data.article_id || null,
            has_medical_review: data.has_medical_review || false,
            is_sponsored: data.is_sponsored || false,
            enable_providers_block: data.enable_providers_block || false,
            author_id: data.author_id || null
        };

        if (data.main_category?.slug) properties.main_category = data.main_category.slug;
        if (data.tags?.length) properties.article_tags = data.tags.map(tag => tag.slug).join(',');
        if (data.categories?.length) properties.article_categories = data.categories.map(cat => cat.slug).join(',');

        const contentArea = document.querySelector('.article-v2__content, .article-v2__content-wrapper');
        const hasBlock = (selector) => (contentArea?.querySelector(selector) || document.querySelector(selector)) !== null;
        
        const blockSelectors = [
            { selector: '.providers-block, .providers-mosaic-swiper', name: 'providers_block' },
            { selector: '.gated-file-block', name: 'gated_file_block' },
            { selector: '.banner--quiz, .banner--test, .test-card', name: 'test_block' },
            { selector: '.article-v2__question', name: 'editor_question_block' },
            { selector: '#recommended-articles', name: 'recommended_articles' },
            { selector: '.newsletter-banner__form, #newsletter-form', name: 'newsletter_banner' },
            { selector: '.article-v2__sidebar .sidebar-card-v2', name: 'table_of_contents' }
        ];
        
        const blocksShown = blockSelectors.filter(b => hasBlock(b.selector)).map(b => b.name);
        if (blocksShown.length) properties.blocks_shown = blocksShown.join(',');
        
        return properties;
    },

    extractProviderSearchProperties() {
        const urlParams = new URLSearchParams(window.location.search);
        const properties = {};
        const filterParams = ['condition', 'symptom', 'service', 'service_delivery', 'country', 'help_with', 'where', 'substance', 'provider_type'];
        
        filterParams.forEach(param => {
            const value = urlParams.get(param);
            if (value) properties[`filter_${param}`] = value;
        });

        const activeFilters = Object.keys(properties).filter(key => key.startsWith('filter_'));
        if (activeFilters.length) {
            properties.filters_active = activeFilters.map(key => key.replace('filter_', '')).join(',');
        }

        const resultsContainer = document.querySelector('#provider-results');
        if (resultsContainer) {
            const isEmpty = resultsContainer.querySelector('[data-filter-empty="true"]') !== null;
            if (isEmpty) {
                properties.results_count = 0;
            } else {
                const visibleItems = Array.from(resultsContainer.querySelectorAll('.mosaic__item:not(.mosaic__empty-response)'))
                    .filter(item => {
                        const style = window.getComputedStyle(item);
                        return style.display !== 'none' && style.visibility !== 'hidden' && item.offsetParent !== null;
                    });
                if (visibleItems.length) properties.results_count = visibleItems.length;
            }
        }

        const pathname = window.location.pathname.toLowerCase();
        if (pathname.startsWith('/providers/type/')) {
            const pathParts = pathname.split('/').filter(part => part.length > 0);
            if (pathParts.length > 2) properties.provider_type_slug = pathParts[2];
        }

        return properties;
    },


    /**
     * Initialize banner tracking
     */
    initBannerTracking() {
        TrackingCore.delegateClick('a[href*="/screening/find-a-provider/"]', (link) => {
            this.trackStandardEvent(TRACKING_EVENTS.BANNERS.CLICKED, {
                banner_type: 'find_the_right_care',
                link_text: link.textContent?.trim() || 'Find the right care'
            });
        });
    },

    /**
     * Initialize hero banner click tracking
     */
    initHeroBannerTracking() {
        const heroCovers = document.querySelectorAll('#home-hero-banner');
        
        heroCovers.forEach(cover => {
            cover.addEventListener('click', function() {
                const bannerUrl = this.href || this.querySelector('a')?.href || window.location.href;
                const bannerText = this.textContent?.trim() || '';
                
                Tracking.trackStandardEvent(TRACKING_EVENTS.BANNERS.CLICKED, {
                    banner_type: 'home_hero',
                    banner_url: bannerUrl,
                    banner_text: bannerText,
                    page_type: 'home'
                });
            });
        });
    },

    /**
     * Initialize header CTA tracking
     */
    initHeaderCTATracking() {
        // Track header "Start Screening" / custom CTA button clicks
        TrackingCore.delegateClick('.header__actions a.btn--accent, .header-new__cta, .off-canvas__actions a.btn--accent', (link) => {
            this.trackStandardEvent(TRACKING_EVENTS.NAVIGATION.HEADER_CTA_CLICKED, {
                cta_text: link.textContent?.trim() || 'Start Screening',
                cta_link: link.href || null,
                cta_location: link.closest('.off-canvas__actions') ? 'mobile_menu' : 'header'
            });
        });

        // Track header "Account" button clicks (sign-in popup trigger)
        // Note: Popup opens are tracked separately via POPUP_OPENED event
        TrackingCore.delegateClick('.header__actions button[data-popup="sign-in"], .off-canvas__actions button[data-popup="sign-in"]', (btn) => {
            this.trackStandardEvent(TRACKING_EVENTS.NAVIGATION.HEADER_CTA_CLICKED, {
                cta_type: 'account_button',
                button_text: btn.textContent?.trim() || 'Account',
                cta_location: btn.closest('.off-canvas__actions') ? 'mobile_menu' : 'header'
            });
        });
    },

    /**
     * Initialize providers block tracking
     */
    initProvidersBlockTracking() {
        document.body.addEventListener('click', (e) => {
            const card = e.target.closest('.providers-block .card-v2, .providers-mosaic-swiper .card-v2, #provider-results .card-v2');
            if (card) {
                this.trackStandardEvent(TRACKING_EVENTS.BANNERS.CLICKED, {
                    banner_type: 'providers_banner',
                    clinic_name: card.dataset.clinicName || card.dataset.providerName || 'Unknown'
                });
            }
        });
    },

    initProviderCollectionFaqTracking() {
        TrackingCore.delegateClick(
            '.provider-collection-faq .faq-item__question',
            (el) => {
                const item = el.closest('.faq-item');
                if (!item) return;
                const questionEl = item.querySelector('.faq-item__text');
                const wasActive = item.classList.contains('--active');
                this.trackStandardEvent(TRACKING_EVENTS.ENGAGEMENT.FAQ_ITEM_CLICKED, {
                    question_text: questionEl?.textContent?.trim() || '',
                    is_opening: !wasActive
                });
            },
            document.body,
            true
        );
    },

    /**
     * Initialize page view tracking
     */
    initPageViewTracking() {
        const pageType = TrackingCore.getPageType();
        const baseProperties = { referrer: document.referrer || null };
        
        if (pageType === 'article_page') Object.assign(baseProperties, this.extractArticleProperties());
        if (pageType === 'providers_list') Object.assign(baseProperties, this.extractProviderSearchProperties());
        
        this.trackStandardEvent(TRACKING_EVENTS.ENGAGEMENT.PAGE_VIEW, baseProperties);
    },

    /**
     * Initialize provider page tracking
     */
    initProviderPageTracking() {
        if (TrackingCore.getPageType() !== 'provider_page') return;

        const buttonConfigs = [
            {
                selector: 'button[data-popup="provider-contact"]',
                event: TRACKING_EVENTS.PROVIDERS.CTA_CLICKED,
                props: (btn) => ({ cta_type: 'book_consultation', ...this.extractProviderProperties(btn) })
            },
            {
                selector: '.provider-single__ecom-cta',
                event: TRACKING_EVENTS.PROVIDERS.CTA_CLICKED,
                props: (btn) => ({
                    cta_type: 'custom_cta',
                    ...this.extractProviderProperties(btn),
                    cta_link: btn.dataset.ctaLink || btn.href || null,
                    cta_button_text: TrackingCore.getButtonText(btn)
                })
            },
        ];

        buttonConfigs.forEach(({ selector, event, props }) => {
            TrackingCore.delegateClick(selector, (btn) => {
                this.trackStandardEvent(event, props(btn));
            });
        });

        TrackingCore.delegateClick('.provider-single__card-social-link', (link) => {
            this.trackStandardEvent(TRACKING_EVENTS.PROVIDERS.CONTACT_CLICKED, {
                contact_type: link.dataset.contactType || 'unknown',
                link_url: link.href || null,
                ...this.extractProviderProperties(link)
            });
        });

        const contactForm = document.getElementById('provider-contact-form');
        if (contactForm) {
            contactForm.addEventListener('submit', () => {
                const triggerButton = document.querySelector('button[data-popup="provider-contact"]');
                const getValue = (name) => contactForm.querySelector(`input[name="${name}"]`)?.value;
                
                this.trackStandardEvent(TRACKING_EVENTS.PROVIDERS.BOOK_CONSULTATION_SENT, {
                    provider_id: getValue('provider_id') || triggerButton?.dataset.providerId || null,
                    provider_name: getValue('provider_name') || triggerButton?.dataset.providerName || null,
                    provider_url: getValue('provider_url') || null,
                    provider_country: triggerButton?.dataset.providerCountry || null,
                    provider_city: triggerButton?.dataset.providerCity || null,
                    provider_type: triggerButton?.dataset.providerType || null,
                    button_location: triggerButton?.dataset.buttonLocation || 'unknown'
                });
            });
        }
    },

    /**
     * Initialize instant booking tracking
     * Tracks clicks on booking popup buttons and instant booking links
     */
    initInstantBookingTracking() {
        const selector = '.provider-single__book-appointment-btn, [class*="amelia-popup-trigger"], a[href*="booking=1"][data-provider-id]';

        TrackingCore.delegateClick(selector, (element) => {
            const props = {
                cta_type: 'instant_booking_click',
                ...this.extractProviderProperties(element)
            };

            this.trackStandardEvent(TRACKING_EVENTS.PROVIDERS.CTA_CLICKED, props);

            const isPopupButton = element.matches('.provider-single__book-appointment-btn') ||
                element.className.includes('amelia-popup-trigger');

            if (isPopupButton) {
                const context = {
                    provider_id: element.dataset.providerId || null,
                    provider_name: element.dataset.providerName || null,
                    button_location: element.dataset.buttonLocation || 'unknown'
                };
                sessionStorage.setItem('pe_mp_booking_context', JSON.stringify(context));
            }
        }, document.body, true);
    },

    /**
     * Initialize scroll tracking
     */
    initScrollTracking() {
        let trackedDepths = [];
        const milestones = [25, 50, 75, 90];
        
        window.addEventListener('scroll', () => {
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            if (docHeight <= 0) return;

            const scrollPercent = (window.scrollY / docHeight) * 100;
            
            milestones.forEach(milestone => {
                if (scrollPercent >= milestone && !trackedDepths.includes(milestone)) {
                    trackedDepths.push(milestone);
                    this.trackStandardEvent(TRACKING_EVENTS.ENGAGEMENT.PAGE_SCROLL, {
                        scroll_depth: milestone,
                        scroll_percent: milestone + '%'
                    });
                }
            });
        });
    },

    /**
     * Initialize logout tracking
     */
    initLogoutTracking() {
        document.body.addEventListener('click', (e) => {
            const link = e.target.closest('a[href*="logout"]');
            if (link && (link.href.includes('action=logout') || link.href.includes('wp-login.php'))) {
                this.trackStandardEvent(TRACKING_EVENTS.AUTH.LOGOUT_CLICKED);
            }
        });
    },

    /**
     * Initialize newsletter tracking
     */
    initNewsletterTracking() {
        document.querySelectorAll('#newsletter-form, .newsletter-banner__form, #newsletter-fresh__form').forEach(form => {
            form.addEventListener('submit', () => this.trackStandardEvent(TRACKING_EVENTS.FORMS.NEWSLETTER_SUBSCRIPTION_ATTEMPTED));
        });
    },

    /**
     * Initialize test report tracking
     * @param {object} context - Report context (result_id, report_type, quiz_id, report_meta)
     */
    initTestReportTracking(context = {}) {
        const { result_id, report_type, quiz_id, report_meta = {} } = context;
        const baseProps = { result_id, report_type, quiz_id, page_type: 'test_report' };

        const providerCount = report_meta.provider_count ?? this._countReportProviders();
        this.trackStandardEvent(TRACKING_EVENTS.TEST.REPORT_VIEWED, {
            ...baseProps,
            ...report_meta,
            provider_count: providerCount
        });
        const trackReportEvent = (eventName, properties = {}) => this.trackStandardEvent(eventName, { ...baseProps, ...properties });

        // Track card clicks
        TrackingCore.trackCardClicks(
            document.querySelectorAll('#recommended-articles .card'),
            trackReportEvent,
            TRACKING_EVENTS.TEST.ARTICLE_CLICKED,
            (card, index) => {
                const { title, url } = TrackingCore.extractCardData(card);
                return { article_title: title, article_url: url, article_position: index + 1, source: 'recommended_articles_block' };
            }
        );

        TrackingCore.trackCardClicks(
            document.querySelectorAll('.banner--test, .test-card'),
            trackReportEvent,
            TRACKING_EVENTS.TEST.TEST_CLICKED,
            (card, index) => {
                const { title, url } = TrackingCore.extractCardData(card, ['.banner__title', '.test-card__title', '.test-title', 'h3', 'h4']);
                return { test_title: title, test_url: url, test_position: index + 1, source: 'recommended_tests_block' };
            }
        );

        TrackingCore.trackCardClicks(
            document.querySelectorAll('#recommended-providers .card, .provider-card'),
            trackReportEvent,
            TRACKING_EVENTS.PROVIDERS.CARD_CLICKED,
            (card, index) => {
                const { title, url } = TrackingCore.extractCardData(card, ['.card__title', '.provider-name', 'h3', 'h4']);
                return { provider_name: title, provider_url: url, provider_position: index + 1, source: 'recommended_providers_block' };
            }
        );

        // Track button clicks
        const buttonConfigs = [
            { selector: '#recommended-articles .btn--muted', event: TRACKING_EVENTS.TEST.SEE_ALL_ARTICLES_CLICKED, source: 'recommended_articles_block' },
            { selector: '.share-trigger', event: TRACKING_EVENTS.TEST.INVITE_OTHERS_CLICKED, source: 'report_actions' },
            { selector: 'a[href="/screening/"]', event: TRACKING_EVENTS.TEST.TAKE_ANOTHER_TEST_CLICKED, source: 'report_actions', 
              condition: (el) => el.textContent.trim().includes('Take another test') },
            { selector: 'a[href*="/tests/find-a-provider/"]', event: TRACKING_EVENTS.TEST.FIND_PROVIDER_TEST_BANNER_CLICKED, 
              source: 'professional_help_banner', condition: (el) => {
                  const text = el.textContent.trim().toLowerCase();
                  return text.includes('start') && text.includes('test');
              }, props: (el) => ({ test_url: el.href }) },
            { selector: '.report-v2__scores-subscales .scores-bar__legend, .subscale-item', 
              event: TRACKING_EVENTS.TEST.CATEGORY_ARROW_CLICKED, source: 'report_subscales',
              props: (el) => ({ category_name: TrackingCore.getButtonText(el) }) },
            { selector: '#recommended-providers .btn--muted', 
              event: TRACKING_EVENTS.TEST.SEE_ALL_TESTS_CLICKED, source: 'recommended_tests_block',
              condition: (el) => el.textContent.trim().includes('See all') },
            { selector: 'a[href*="go-microdose.com"]', event: TRACKING_EVENTS.BANNERS.CLICKED, 
              source: 'microdosing_banner', props: (el) => ({ banner_type: 'microdosing', banner_url: el.href }) }
        ];

        buttonConfigs.forEach(({ selector, event, source, condition, props }) => {
            TrackingCore.delegateClick(selector, (element) => {
                if (condition && !condition(element)) return;
                trackReportEvent(event, {
                    button_text: TrackingCore.getButtonText(element),
                    source,
                    ...(props ? props(element) : {})
                });
            });
        });

        this.trackSocialButtons(trackReportEvent);
    },

    _countReportProviders() {
        const cards = document.querySelectorAll('.report-v2__provider-cards .mosaic__item, #recommended-providers .card-v2, #recommended-providers .card, .provider-card');
        return cards.length;
    },

    /**
     * Track social media button clicks
     * @param {function} trackFn - Optional custom tracking function
     */
    trackSocialButtons(trackFn = null) {
        const tracker = trackFn || ((eventName, props) => TrackingCore.trackEvent(eventName, props));

        const socialConfig = {
            'discord.gg': { event: TRACKING_EVENTS.ENGAGEMENT.DISCORD_BUTTON_CLICKED, prop: 'discord_url' },
            'discord.com': { event: TRACKING_EVENTS.ENGAGEMENT.DISCORD_BUTTON_CLICKED, prop: 'discord_url' },
            'facebook.com': { event: TRACKING_EVENTS.ENGAGEMENT.FACEBOOK_BUTTON_CLICKED, prop: 'facebook_url' },
            'fb.com': { event: TRACKING_EVENTS.ENGAGEMENT.FACEBOOK_BUTTON_CLICKED, prop: 'facebook_url' },
            'whatsapp.com': { event: TRACKING_EVENTS.ENGAGEMENT.WHATSAPP_BUTTON_CLICKED, prop: 'whatsapp_url' },
            'wa.me': { event: TRACKING_EVENTS.ENGAGEMENT.WHATSAPP_BUTTON_CLICKED, prop: 'whatsapp_url' }
        };

        TrackingCore.delegateClick('a[href*="discord"], a[href*="facebook"], a[href*="fb.com"], a[href*="whatsapp"], a[href*="wa.me"]', (link) => {
            const href = link.href.toLowerCase();
            const config = Object.entries(socialConfig).find(([domain]) => href.includes(domain));
            
            if (config) {
                tracker(config[1].event, {
                    button_text: TrackingCore.getButtonText(link),
                    [config[1].prop]: link.href,
                    source: 'social_buttons'
                });
            }
        });
    },

    /**
     * Initialize SSO tracking
     */
    initSSOTracking() {
        TrackingCore.delegateClick('a[data-provider][data-action="connect"], a[href*="loginSocial="]', (link) => {
            const provider = link.dataset.provider || (link.href.includes('loginSocial=google') ? 'google' : null);
            
            if (provider) {
                this.trackStandardEvent(TRACKING_EVENTS.AUTH.SSO_CLICKED, {
                    sso_provider: provider,
                    redirect_url: link.dataset.redirect || link.href || null,
                });
            }
        });
    },

    /**
     * Initialize auth success tracking
     */
    initAuthSuccessTracking() {
        const urlParams = new URLSearchParams(window.location.search);
        const magicLogin = urlParams.get('magic_login');
        const ssoProvider = urlParams.get('sso_provider');
        const ssoNewUser = urlParams.get('sso_new_user') === '1';
        
        // Check if user just logged in (either via magic link or SSO)
        const isAuthSuccess = (magicLogin === 'success' || ssoProvider) && typeof window.pe_mp_user_data !== 'undefined';
        
        if (isAuthSuccess) {
            const userData = window.pe_mp_user_data;
            const isRegistered = Boolean(userData.is_registered);
            const userId = userData.user_id;
            
            if (isRegistered && userId) {
                // CRITICAL: Re-identify user in Amplitude after successful login
                // This updates the user identification so subsequent events are associated with the user
                TrackingCore.identifyUser(userData);
                
                // Check if this is a new registration
                // For magic links: check sessionStorage flag
                // For SSO: check URL parameter from backend
                const isNewRegistration = sessionStorage.getItem('pending_registration') === 'true' || ssoNewUser;
                
                // Clear the flags
                sessionStorage.removeItem('pending_registration');
                
                const eventName = isNewRegistration 
                    ? TRACKING_EVENTS.AUTH.REGISTRATION_SUCCESS 
                    : TRACKING_EVENTS.AUTH.LOGIN_SUCCESS;
                
                const eventProps = {
                    // Note: user_id will be automatically formatted in trackEvent()
                    // Don't include raw user_id here to avoid overriding formatted version
                    is_registered: true,
                    is_admin: Boolean(userData.is_admin),
                };
                
                // Add auth method to distinguish magic link vs SSO
                if (ssoProvider) {
                    eventProps.auth_method = 'sso';
                    eventProps.sso_provider = ssoProvider;
                } else if (magicLogin === 'success') {
                    eventProps.auth_method = 'magic_link';
                }
                
                this.trackStandardEvent(eventName, eventProps);
                
                // Clean up URL parameters
                const newUrl = window.location.pathname + (window.location.hash || '');
                window.history.replaceState({}, '', newUrl);
            }
        }
    },

    /**
     * Initialize all tracking
     * Add new initXxxTracking() methods here
     */
    init() {
        if (typeof window.pe_mp_user_data !== 'undefined') {
            TrackingCore.identifyUser(window.pe_mp_user_data);
        }
        
        this.initAuthSuccessTracking();
        this.initSSOTracking();
        this.initPageViewTracking();
        this.initBannerTracking();
        this.initHeroBannerTracking();
        this.initHeaderCTATracking();
        this.initProvidersBlockTracking();
        this.initProviderCollectionFaqTracking();
        this.initProviderPageTracking();
        this.initInstantBookingTracking();
        this.initLogoutTracking();
        this.initNewsletterTracking();
        this.initScrollTracking();
        
        // Initialize social buttons tracking (if not on test report page or screening page)
        if (!document.body.classList.contains('page-template-test-report') && TrackingCore.getPageType() !== 'screening_page') {
            this.trackSocialButtons();
        }
    },

    /**
     * Debug mode control (wrapper)
     */
    setDebugMode(enabled) {
        TrackingDebug.setDebugMode(enabled);
    },

};

// Make available globally for inline scripts
window.Tracking = Tracking;

// Expose debug toggle for easy access
// Usage: Tracking.setDebugMode(true) or window.TRACKING_DEBUG = true
// Or add ?tracking_debug=true to URL

// Export for ES6 modules
export default Tracking;
